import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import prospectService from '../services/prospectService';
import workflowService from '../services/workflowService';
import projectService from '../services/projectService';
import LeadImportModal from '../components/LeadImportModal';
import LeadDrawer from '../components/LeadDrawer';
import LeadActionBar from '../components/LeadActionBar';
import Icon from '../components/Icon';
import Button from '../components/Button';
import SaveToPoolModal from '../components/SaveToPoolModal';

/**
 * The ledger.
 *
 * Everyone Orion has found or been given, as one record per person rather than
 * a fresh row per run. At 3,500 rows the job stops being "look at my leads" and
 * becomes "find the forty I should work today", so the page is built around
 * narrowing rather than around browsing:
 *
 *   pools on the left · filters across the top · one row per person
 *
 * Three things drive the layout. Lists were a dropdown among three others and
 * were forgotten; they are a rail now, because a pool is a place you go rather
 * than a filter you remember to set. The four stat tiles were the loudest thing
 * on the page and the least useful — four numbers nobody could act on — so they
 * are chips that filter when clicked, at a quarter of the height. And the
 * selection bar used to be inserted above the table, which shoved the list down
 * every time you ticked a box; it sticks to the bottom now and the table holds
 * still.
 */

const Page = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.35rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.015em;
  margin: 0 0 0.3rem;
`;

const Lede = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-base);
  line-height: 1.6;
  margin: 0;
  max-width: 62ch;
`;

/* Rail and table, side by side, collapsing to one column on a narrow screen. */
const Split = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Rail = styled.nav`
  position: sticky;
  top: 1rem;

  @media (max-width: 940px) {
    position: static;
  }
`;

const RailTitle = styled.div`
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0 0.6rem 0.5rem;
`;

/*
 * A pool row.
 *
 * A div rather than a button because it contains the rename and delete buttons,
 * and nesting a button inside a button is invalid HTML that browsers resolve by
 * dropping the inner one. It carries the role and the key handling instead, so
 * it is still reachable from the keyboard.
 */
const RailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: ${(p) => (p.$on ? 'var(--text-primary)' : 'var(--text-secondary)')};
  background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'transparent')};
  font-size: var(--text-sm);

  &:hover { background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'var(--surface-hover)')}; }
  &:hover .rail-menu { opacity: 1; }
`;

const RailName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RailCount = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
`;

const RailMenu = styled.button`
  opacity: 0;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover, &:focus-visible { opacity: 1; color: var(--text-primary); background: var(--surface-hover); }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

/* The search box and its field selector read as one control, because they are. */
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 260px;
  background: var(--surface-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding-left: 0.65rem;
  color: var(--text-muted);

  &:focus-within { border-color: var(--border-accent); }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 0.5rem 0.6rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: var(--text-sm);

  &:focus { outline: none; }
`;

const ScopeSelect = styled.select`
  background: transparent;
  border: none;
  border-left: 1px solid var(--border-color);
  border-radius: 0;
  padding: 0.5rem 0.55rem;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: var(--text-xs);
  width: auto;

  &:focus { outline: none; }
  option { background: var(--surface-1); color: var(--text-primary); }
`;

const Select = styled.select`
  background: var(--surface-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.5rem 0.6rem;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: var(--text-sm);
  width: auto;

  &:focus { outline: none; border-color: var(--border-accent); }
  option { background: var(--surface-1); color: var(--text-primary); }
`;

/* The old stat tiles, at a quarter of the height and clickable. */
const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-family: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? 'var(--border-accent)' : 'var(--border-color)')};
  background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--text-primary)' : 'var(--text-secondary)')};

  &:hover { border-color: var(--border-strong); color: var(--text-primary); }
`;

const ChipCount = styled.span`
  color: ${(p) => p.$color || 'var(--text-muted)'};
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

/* Only appears when there is more to select than is on screen. */
const SelectAllLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--border-accent);
  background: var(--accent-cyan-dim);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--text-sm);
`;

const TableWrap = styled.div`
  background: var(--surface-1);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 820px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: var(--text-sm);
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  font-size: var(--text-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
  background: var(--surface-2);
  position: sticky;
  top: 0;
  z-index: 2;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: ${(p) => (p.$on ? 'var(--accent-cyan)' : 'inherit')};
  cursor: pointer;

  &:hover { color: var(--text-primary); }
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--border-color);

  &:last-child { border-bottom: none; }
  &:hover { background: var(--surface-hover); }
`;

const Td = styled.td`
  padding: 0.55rem 0.85rem;
  color: var(--text-secondary);
  vertical-align: middle;
`;

/* The checkbox column holds no text, so it must not try to trim any. */
const CheckCell = styled.td`
  padding: 0.55rem 0.5rem 0.55rem 0.85rem;
  vertical-align: middle;
`;

/* One line, trimmed. A three-line job title makes every row a different height. */
const Line = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => (p.$muted ? 'var(--text-muted)' : 'var(--text-primary)')};
  font-size: ${(p) => (p.$muted ? 'var(--text-xs)' : 'inherit')};
`;

const RowButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  text-align: left;

  &:hover { color: var(--accent-cyan); }
`;

const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--text-secondary);
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 0.08rem 0.42rem;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$border};
  white-space: nowrap;
`;

const Check = styled.input`
  width: auto;
  margin: 0;
  accent-color: var(--accent-cyan);
  cursor: pointer;
`;

const Empty = styled.div`
  padding: 3.5rem 2rem;
  text-align: center;
  color: var(--text-secondary);
  line-height: 1.7;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.9rem;
  color: var(--text-secondary);
  font-size: var(--text-sm);
`;

const PageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  padding: 0.4rem 0.8rem;
  font-family: inherit;
  font-size: var(--text-sm);
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:not(:disabled):hover { color: var(--text-primary); border-color: var(--border-strong); }
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  border: 1px solid ${(p) => (p.$bad ? 'rgba(248,113,113,0.4)' : 'var(--border-accent)')};
  background: ${(p) => (p.$bad ? 'var(--error-dim)' : 'var(--accent-cyan-dim)')};
  border-radius: var(--radius-md);
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.85rem;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.55;
`;

const BannerButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--accent-cyan);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

/**
 * The column somebody actually acts on, so it reports what is known rather than
 * just printing an address. "Unproven" means a catch-all domain: a real address
 * that no mail server would confirm.
 */
function EmailCell({ lead }) {
  if (!lead.email) {
    return lead.linkedinUrl
      ? <Muted>LinkedIn only</Muted>
      : <Muted>No address</Muted>;
  }

  // Only "verified" earns the word confirmed. Treating everything that is not
  // risky as confirmed would put that label on addresses whose status nobody
  // ever established - older records, or a CSV somebody uploaded.
  const label = lead.emailStatus === 'verified'
    ? (lead.emailProvider === 'manual' ? 'added by you' : 'confirmed')
    : lead.emailStatus === 'risky' ? 'unproven' : 'unverified';

  const tone = {
    'confirmed': { bg: 'var(--success-dim)', fg: 'var(--success-color)', border: 'rgba(52,211,153,0.35)', hint: 'Confirmed by the mail server' },
    'added by you': { bg: 'var(--success-dim)', fg: 'var(--success-color)', border: 'rgba(52,211,153,0.35)', hint: 'Typed in by somebody on your team' },
    'unproven': { bg: 'var(--warning-dim)', fg: 'var(--warning-color)', border: 'rgba(251,191,36,0.35)', hint: 'Catch-all domain: the mailbox could not be confirmed' },
    'unverified': { bg: 'rgba(148,163,184,0.10)', fg: '#94a3b8', border: 'rgba(148,163,184,0.30)', hint: 'Nobody has checked whether this address is deliverable' }
  }[label];

  return (
    <>
      <div style={{ color: 'var(--text-primary)' }}>{lead.email}</div>
      <Chip $bg={tone.bg} $fg={tone.fg} $border={tone.border} title={tone.hint}>{label}</Chip>
    </>
  );
}

/** Machine state names, shortened to what a person would say. */
const STATE_LABEL = {
  pending_approval: 'awaiting you',
  follow_up_1: 'follow-up 1',
  follow_up_2: 'follow-up 2',
  invite_sent: 'invited',
  messaged: 'messaged',
  scheduled: 'scheduled',
  completed: 'done',
  replied: 'replied',
  withdrawn: 'withdrawn',
  halted: 'stopped',
  failed: 'failed'
};

function StatusCell({ lead }) {
  if (lead.suppressed) {
    return <Chip $bg="var(--error-dim)" $fg="var(--error-color)" $border="rgba(248,113,113,0.35)">suppressed</Chip>;
  }
  if (!lead.engagement) return <Muted>Not contacted</Muted>;

  return (
    <Chip $bg="rgba(96,165,250,0.10)" $fg="var(--info-color)" $border="rgba(96,165,250,0.32)">
      {STATE_LABEL[lead.engagement.state] || String(lead.engagement.state).replace(/_/g, ' ')}
    </Chip>
  );
}

/**
 * What enrichment can be asked to find, with Clay's published per-row cost.
 * Shown on the toggles so the difference is visible at the moment of choosing:
 * a mobile is roughly ten times an address.
 */
const CAPABILITY_OPTIONS = [
  { key: 'work_email', label: 'Work email', perRow: 1.1 },
  { key: 'mobile_phone', label: 'Mobile phone', perRow: 11.7 }
];

const SEARCH_SCOPES = [
  { value: '', label: 'Anything' },
  { value: 'name', label: 'Name' },
  { value: 'company', label: 'Company' },
  { value: 'title', label: 'Title' },
  { value: 'email', label: 'Email' }
];

const COLUMNS = [
  { key: 'name', label: 'Person', sort: 'name', width: '22%' },
  { key: 'company', label: 'Company', sort: 'company', width: '20%' },
  { key: 'email', label: 'Email', sort: 'email', width: '24%' },
  { key: 'signal', label: 'Why they came up', width: '20%' },
  { key: 'status', label: 'Status', width: '14%' }
];

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('');
  const [emailStatus, setEmailStatus] = useState('all');
  const [signalFilter, setSignalFilter] = useState('');
  const [contacted, setContacted] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [sort, setSort] = useState('');
  const [direction, setDirection] = useState('desc');

  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState('all');
  const [enriching, setEnriching] = useState(false);
  const [outcome, setOutcome] = useState(null);
  // What to look for. Email only by default: a mobile costs roughly ten times
  // as much per lead, so it should be a deliberate choice rather than a bundle.
  const [capabilities, setCapabilities] = useState(['work_email']);
  const [cost, setCost] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [launching, setLaunching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Selecting beyond the page is a second, explicit act: ticking the header
  // box takes the fifty on screen, and a line then offers all of them.
  const [selectingAll, setSelectingAll] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** Exactly what the row query is filtered by, for the ids endpoint. */
  const currentFilters = useCallback(() => ({
    search, searchField, emailStatus, signal: signalFilter, list: listFilter, contacted
  }), [search, searchField, emailStatus, signalFilter, listFilter, contacted]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await prospectService.list({
        page, limit: 50, search, searchField, emailStatus,
        signal: signalFilter, list: listFilter, contacted, sort, direction
      });
      setLeads(data.prospects || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError('Could not load leads.');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, searchField, emailStatus, signalFilter, listFilter, contacted, sort, direction]);

  useEffect(() => {
    // Debounced, so typing a search does not fire a request per keystroke.
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    prospectService.stats().then(setStats).catch(() => setStats(null));
  }, [total]);

  const loadLists = useCallback(
    () => prospectService.lists().then(setLists).catch(() => setLists([])),
    []
  );

  useEffect(() => {
    prospectService.providers().then(setProviders).catch(() => setProviders([]));
    loadLists();

    // Only workflows with a drafting stage can act on a selection - sending
    // people to a sourcing-only workflow would do nothing with them.
    workflowService.getWorkflows()
      .then((res) => {
        const all = (res.data && res.data.workflows) || [];
        setCampaigns(all.filter((w) =>
          (w.nodeTypes || []).some((t) => t === 'draft_email' || t === 'linkedin_message')));
      })
      .catch(() => setCampaigns([]));
  }, [loadLists]);

  useEffect(() => {
    if (!selected.length || !capabilities.length) { setCost(null); return; }
    let cancelled = false;
    prospectService.estimateEnrichment(selected, capabilities)
      .then((c) => { if (!cancelled) setCost(c); })
      .catch(() => { if (!cancelled) setCost(null); });
    return () => { cancelled = true; };
  }, [selected, capabilities]);

  /** Changing what is shown always returns to the first page of it. */
  const change = (setter) => (value) => { setter(value); setPage(1); };

  const sortBy = (field) => {
    if (sort === field) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setDirection('asc');
    }
    setPage(1);
  };

  const pageIds = leads.map((l) => l.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleAll = () => setSelected((prev) =>
    allOnPageSelected ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]);

  const toggleCapability = (key) => setCapabilities((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const runEnrichment = async (force = false) => {
    if (!selected.length || !capabilities.length) return;
    try {
      setEnriching(true);
      setOutcome(null);
      const result = await prospectService.enrich(selected, { provider, force, capabilities });
      setOutcome(result);
      setSelected([]);
      await load();
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Enrichment failed' });
    } finally {
      setEnriching(false);
    }
  };

  const selectAllMatching = async () => {
    try {
      setSelectingAll(true);
      const { ids } = await prospectService.matchingIds(currentFilters());
      setSelected(ids);
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Could not select them all' });
    } finally {
      setSelectingAll(false);
    }
  };

  const suppressSelected = async () => {
    if (!window.confirm(
      `Stop contacting ${selected.length} lead${selected.length === 1 ? '' : 's'}? `
      + 'They stay in Leads, and no campaign will write to them again.'
    )) return;
    try {
      const { suppressed } = await prospectService.suppress(selected);
      setOutcome({
        campaign: `${suppressed} lead${suppressed === 1 ? '' : 's'} will not be contacted again.`
      });
      setSelected([]);
      await load();
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Could not suppress those leads' });
    }
  };

  /**
   * Into a new pool, or one that already exists.
   *
   * Adding to an existing pool is the obvious next thing and was impossible:
   * saving always created, so growing a pool meant making a duplicate and
   * tidying up afterwards.
   */
  const saveToPool = async ({ name, listId }) => {
    const n = selected.length;
    const where = listId
      ? (await prospectService.addToList(listId, selected) && (lists.find((l) => l._id === listId) || {}).name)
      : (await prospectService.createList(name, selected)).list.name;

    await loadLists();
    setOutcome({ campaign: `Saved ${n} lead${n === 1 ? '' : 's'} to "${where}".` });
    setSelected([]);
  };

  /** The current selection as a CSV, in the columns the importer understands. */
  /*
   * The file comes from the server, over the same filters the rows use.
   *
   * It was assembled here from `leads` - the rows already loaded - so exporting
   * a selection that spanned more than one page silently exported the page and
   * apologised for it afterwards.
   */
  const exportSelected = async () => {
    try {
      setExporting(true);
      const blob = await prospectService.exportCsv(currentFilters());
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orion-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Could not export those leads' });
    } finally {
      setExporting(false);
    }
  };

  const launchCampaign = async () => {
    if (!campaignId || !selected.length) return;

    // Drafting is the expensive step and approving is the irreversible one, so
    // the moment deserves a sentence rather than starting on a single click.
    const name = (campaigns.find((c) => String(c.id) === String(campaignId)) || {}).name || 'this campaign';
    if (!window.confirm(
      `Run "${name}" on ${selected.length.toLocaleString()} lead${selected.length === 1 ? '' : 's'}?\n\n`
      + 'Each one is researched and an email drafted, which costs credits. '
      + 'Nothing sends until you approve it in Approvals.'
    )) return;

    try {
      setLaunching(true);
      await workflowService.executeWorkflow(campaignId, { presetProspectIds: selected });
      const name = (campaigns.find((c) => String(c.id) === String(campaignId)) || {}).name || 'the campaign';
      setOutcome({ campaign: `Run started on ${selected.length} lead${selected.length === 1 ? '' : 's'} via ${name}. Drafts appear in Approvals — nothing sends without you.` });
      setSelected([]);
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || err.message || 'Could not start the run' });
    } finally {
      setLaunching(false);
    }
  };

  const renameList = async (list) => {
    const name = window.prompt('Rename this list', list.name);
    if (!name || !name.trim() || name.trim() === list.name) return;
    try {
      await prospectService.renameList(list._id, name.trim());
      await loadLists();
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Could not rename that list' });
    }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`Delete "${list.name}"? The ${list.count} lead${list.count === 1 ? '' : 's'} in it stay in Leads.`)) return;
    try {
      await prospectService.deleteList(list._id);
      if (listFilter === list._id) setListFilter('');
      await loadLists();
    } catch (err) {
      setOutcome({ error: err?.response?.data?.message || 'Could not delete that list' });
    }
  };

  const byStatus = (stats && stats.byEmailStatus) || {};
  const statusChips = [
    { value: 'all', label: 'All', count: stats ? stats.total : null },
    { value: 'verified', label: 'Confirmed email', count: byStatus.verified || 0, color: 'var(--success-color)' },
    { value: 'risky', label: 'Unproven', count: byStatus.risky || 0, color: 'var(--warning-color)' },
    { value: 'unavailable', label: 'LinkedIn only', count: byStatus.unavailable || 0, color: '#94a3b8' }
  ];

  const filtered = search || emailStatus !== 'all' || signalFilter || contacted || listFilter;

  return (
    <Page>
      <Head>
        <div>
          <Title>Leads</Title>
          <Lede>
            Everyone Orion has found for you. Each person is one record that improves
            as campaigns learn more about them, not a fresh row for every run.
          </Lede>
        </div>
        <Button $variant="primary" onClick={() => setImporting(true)}>
          <Icon name="upload" size={15} /> Import a file
        </Button>
      </Head>

      <Split>
        <Rail aria-label="Lead pools">
          <RailTitle>Pools</RailTitle>
          <RailRow
            role="button"
            tabIndex={0}
            $on={!listFilter}
            onClick={() => change(setListFilter)('')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); change(setListFilter)(''); } }}
          >
            <Icon name="layers" size={15} />
            <RailName>All leads</RailName>
            <RailCount>{stats ? stats.total : ''}</RailCount>
          </RailRow>
          {lists.map((list) => (
            <RailRow
              key={list._id}
              role="button"
              tabIndex={0}
              $on={listFilter === list._id}
              onClick={() => change(setListFilter)(list._id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); change(setListFilter)(list._id); } }}
            >
              <Icon name={list.source === 'discovery' ? 'spark' : 'list'} size={15} />
              <RailName title={list.name}>{list.name}</RailName>
              <RailCount>{list.count}</RailCount>
              <RailMenu
                className="rail-menu"
                aria-label={`Rename ${list.name}`}
                onClick={(e) => { e.stopPropagation(); renameList(list); }}
              >
                <Icon name="edit" size={13} />
              </RailMenu>
              <RailMenu
                className="rail-menu"
                aria-label={`Delete ${list.name}`}
                onClick={(e) => { e.stopPropagation(); deleteList(list); }}
              >
                <Icon name="trash" size={13} />
              </RailMenu>
            </RailRow>
          ))}
          {lists.length === 0 && (
            <RailRow as="div" style={{ cursor: 'default' }}>
              <Muted>No pools yet. Importing a file makes one.</Muted>
            </RailRow>
          )}
        </Rail>

        <div>
          <Toolbar>
            <SearchBox>
              <Icon name="search" size={15} />
              <SearchInput
                placeholder="Search leads…"
                value={search}
                onChange={(e) => change(setSearch)(e.target.value)}
                aria-label="Search leads"
              />
              {/* Scoping is what turns "Director" from noise into a query: an
                  unscoped search also matches a company called Director Films. */}
              <ScopeSelect
                value={searchField}
                onChange={(e) => change(setSearchField)(e.target.value)}
                aria-label="Which field to search"
              >
                {SEARCH_SCOPES.map((s) => <option key={s.value} value={s.value}>in {s.label}</option>)}
              </ScopeSelect>
            </SearchBox>

            <Select value={contacted} onChange={(e) => change(setContacted)(e.target.value)} aria-label="Contacted">
              <option value="">Contacted or not</option>
              <option value="no">Not contacted</option>
              <option value="yes">Already contacted</option>
            </Select>

            <Select value={signalFilter} onChange={(e) => change(setSignalFilter)(e.target.value)} aria-label="Signal">
              <option value="">Any signal</option>
              <option value="new">New signals</option>
              <option value="any">Has a signal</option>
              <option value="stale">Signal is old</option>
              <option value="none">No signal</option>
            </Select>
          </Toolbar>

          <Chips>
            {statusChips.map((chip) => (
              <FilterChip
                key={chip.value}
                $on={emailStatus === chip.value}
                onClick={() => change(setEmailStatus)(chip.value)}
              >
                {chip.label}
                <ChipCount $color={chip.color}>{chip.count === null ? '—' : chip.count.toLocaleString()}</ChipCount>
              </FilterChip>
            ))}
          </Chips>

          {outcome && (
            <Banner $bad={Boolean(outcome.error)}>
              <Icon name={outcome.error ? 'alert' : 'info'} size={15} style={{ marginTop: '0.1rem' }} />
              <div style={{ flex: 1 }}>
                {outcome.error || outcome.campaign || (
                  <>
                    {outcome.found} address{outcome.found === 1 ? '' : 'es'} found from {outcome.attempted} looked up
                    {outcome.skipped > 0 && `, ${outcome.skipped} skipped as asked recently`}.
                    {outcome.skipped > 0 && (
                      <> <BannerButton onClick={() => runEnrichment(true)} disabled={enriching}>Check again anyway</BannerButton></>
                    )}
                  </>
                )}
              </div>
              <BannerButton onClick={() => setOutcome(null)}>Dismiss</BannerButton>
            </Banner>
          )}

          {/* Going beyond the page is a second, explicit act. Ticking the
              header box takes the fifty on screen; this offers the rest. */}
          {allOnPageSelected && total > leads.length && selected.length < total && (
            <SelectAllLine>
              All {leads.length} on this page are selected.
              <BannerButton onClick={selectAllMatching} disabled={selectingAll}>
                {selectingAll ? 'Selecting…' : `Select all ${total.toLocaleString()} matching`}
              </BannerButton>
            </SelectAllLine>
          )}

          {selected.length > leads.length && (
            <SelectAllLine>
              All {selected.length.toLocaleString()} matching leads are selected.
              <BannerButton onClick={() => setSelected([])}>Clear</BannerButton>
            </SelectAllLine>
          )}

          <TableWrap>
            {loading && leads.length === 0 ? (
              <Empty>Loading leads…</Empty>
            ) : error ? (
              <Empty>{error}</Empty>
            ) : leads.length === 0 ? (
              <Empty>
                {filtered
                  ? 'No leads match those filters.'
                  : 'No leads yet. Run a campaign, or import a file, and everyone lands here.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: '34px' }}>
                      <Check
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleAll}
                        aria-label="Select everyone on this page"
                      />
                    </Th>
                    {COLUMNS.map((col) => (
                      <Th key={col.key} style={{ width: col.width }}>
                        {col.sort ? (
                          <SortButton $on={sort === col.sort} onClick={() => sortBy(col.sort)}>
                            {col.label}
                            {sort === col.sort && (
                              <Icon name={direction === 'asc' ? 'chevronUp' : 'chevronDown'} size={12} />
                            )}
                          </SortButton>
                        ) : col.label}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <Tr key={lead.id}>
                      <CheckCell>
                        <Check
                          type="checkbox"
                          checked={selected.includes(lead.id)}
                          onChange={() => toggle(lead.id)}
                          aria-label={`Select ${lead.fullName || 'this lead'}`}
                        />
                      </CheckCell>
                      <Td>
                        <Line>
                          <RowButton type="button" onClick={() => setOpen(lead)} title={lead.fullName || undefined}>
                            {lead.fullName || 'Unknown'}
                          </RowButton>
                        </Line>
                        <Line $muted title={lead.title || undefined}>{lead.title || 'Title unknown'}</Line>
                      </Td>
                      <Td>
                        <Line title={lead.organizationName || undefined}>{lead.organizationName || '—'}</Line>
                        {lead.domain && <Line $muted>{lead.domain}</Line>}
                      </Td>
                      <Td><EmailCell lead={lead} /></Td>
                      <Td>
                        {lead.signal ? (
                          <>
                            <Clamp style={{ opacity: lead.signalStale ? 0.55 : 1 }} title={lead.signal}>{lead.signal}</Clamp>
                            <Muted>
                              {lead.signalType}
                              {lead.signalAt && ` · ${new Date(lead.signalAt).toLocaleDateString()}`}
                              {lead.signalStale && ' · old news'}
                            </Muted>
                          </>
                        ) : <Muted>—</Muted>}
                      </Td>
                      <Td><StatusCell lead={lead} /></Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableWrap>

          <Pager>
            <span>{total.toLocaleString()} lead{total === 1 ? '' : 's'}</span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <PageButton disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <Icon name="chevronLeft" size={13} /> Previous
              </PageButton>
              <span>Page {page} of {totalPages}</span>
              <PageButton disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next <Icon name="chevronRight" size={13} />
              </PageButton>
            </div>
          </Pager>

          {selected.length > 0 && (
            <LeadActionBar
              selected={selected}
              providers={providers}
              provider={provider}
              setProvider={setProvider}
              capabilities={capabilities}
              toggleCapability={toggleCapability}
              capabilityOptions={CAPABILITY_OPTIONS}
              cost={cost}
              enriching={enriching}
              onEnrich={runEnrichment}
              campaigns={campaigns}
              campaignId={campaignId}
              setCampaignId={setCampaignId}
              launching={launching}
              onLaunch={launchCampaign}
              onSaveAsList={() => setSaving(true)}
              onSuppress={suppressSelected}
              onExport={exportSelected}
              onExporting={exporting}
              onClear={() => { setSelected([]); setOutcome(null); }}
            />
          )}
        </div>
      </Split>

      {saving && (
        <SaveToPoolModal
          count={selected.length}
          pools={lists}
          onClose={() => setSaving(false)}
          onSave={saveToPool}
        />
      )}

      {open && (
        <LeadDrawer
          lead={open}
          lists={lists}
          onClose={() => setOpen(null)}
          onChanged={(updated) => {
            // Patch the row in place rather than refetching: the page is 50
            // rows and a reload would lose the scroll position mid-edit.
            setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
          }}
          onDeleted={(id) => {
            setLeads((prev) => prev.filter((l) => l.id !== id));
            setSelected((prev) => prev.filter((x) => x !== id));
            setTotal((t) => Math.max(0, t - 1));
          }}
        />
      )}

      {importing && (
        <LeadImportModal
          projectId={projectService.current()}
          onClose={() => setImporting(false)}
          onImported={async (result) => {
            // The rows just imported are what somebody wants to look at, so the
            // page filters to that pool rather than dropping them into the pile.
            await loadLists();
            if (result && result.list) {
              setListFilter(result.list.id);
              setPage(1);
            }
            await load();
          }}
        />
      )}
    </Page>
  );
};

export default Leads;
